import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API } from '../App';
import { ShieldCheck, Search, FileCheck, CheckCircle2, ArrowRight, ArrowLeft, Store } from 'lucide-react';
import { formatPlaceName } from '../utils/placeName';
import LanguageToggle from '../components/LanguageToggle';
import axios from 'axios';

const ClaimYourBusiness = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // STAGE 1: the "Claim this Business" model is retired. This page now shows a
  // graceful notice instead of the old search/claim flow (route kept so old
  // links/bookmarks don't 404). All six languages + Arabic RTL supported.
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="bg-gradient-to-r from-amber-500 to-orange-500">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="text-white inline-flex items-center gap-2 text-sm font-medium" data-testid="claim-retired-back">
            <ArrowLeft className="h-4 w-4" />{t('backToHome', 'Back to home')}
          </button>
          <LanguageToggle />
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center" data-testid="claim-retired-notice">
          <div className="mx-auto h-14 w-14 rounded-full bg-amber-50 flex items-center justify-center mb-4">
            <Store className="h-7 w-7 text-amber-600" />
          </div>
          <h1 className="text-lg font-semibold text-gray-900">{t('claimRetiredTitle', 'Claiming is no longer available')}</h1>
          <p className="text-sm text-gray-500 mt-2">{t('claimRetiredBody', 'Find-2-Day now works by self-registration. If you run a business, simply create your own listing — no need to claim an existing record.')}</p>
          <button
            onClick={() => navigate('/business/signup')}
            className="mt-6 w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center justify-center gap-2"
            data-testid="claim-retired-register-btn"
          >
            {t('claimRetiredRegister', 'Register your business')}<ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const _ClaimYourBusinessLegacy = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/businesses`, { params: { search: query.trim() } });
      setSearchResults(res.data.filter(b => b.source === 'seed' && !b.isClaimed));
    } catch {
      setSearchResults([]);
    }
    setSearching(false);
  };

  const steps = [
    { icon: Search, title: t('claimStep1Title'), desc: t('claimStep1Desc') },
    { icon: FileCheck, title: t('claimStep2Title'), desc: t('claimStep2Desc') },
    { icon: CheckCircle2, title: t('claimStep3Title'), desc: t('claimStep3Desc') },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-white/80 hover:text-white transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            {t('goBack')}
          </button>
          <LanguageToggle />
        </div>
      </div>

      {/* Hero section */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 pb-16 pt-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-6 backdrop-blur-sm">
            <ShieldCheck className="h-10 w-10" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="claim-page-title">
            {t('claimPageTitle')}
          </h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-8">
            {t('claimPageSubtitle')}
          </p>
          <button
            onClick={() => navigate('/explore')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-amber-600 font-bold text-lg rounded-xl shadow-lg hover:shadow-xl hover:bg-gray-50 transition-all"
            data-testid="find-my-business-btn"
          >
            {t('claimFindMyBusiness')}
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Quick Search Section */}
      <div className="max-w-4xl mx-auto px-6 -mt-8 mb-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          <h2 className="text-xl font-bold text-gray-900 text-center mb-4" data-testid="search-section-title">
            {t('claimSearchTitle', 'Search for Your Business')}
          </h2>
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('claimSearchPlaceholder', 'Enter your business name...')}
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                data-testid="claim-search-input"
              />
            </div>
            <button
              type="submit"
              disabled={searching}
              className="px-6 py-3 bg-amber-500 text-white font-semibold rounded-xl hover:bg-amber-600 transition-colors disabled:opacity-50"
              data-testid="claim-search-btn"
            >
              {searching ? '...' : t('claimSearchBtn', 'Search')}
            </button>
          </form>

          {/* Search Results */}
          {searchResults !== null && (
            <div className="mt-4" data-testid="claim-search-results">
              {searchResults.length === 0 ? (
                <p className="text-gray-500 text-center py-3">
                  {t('claimSearchNoResults', 'No unclaimed businesses found with that name. Try a different search or browse all listings.')}
                </p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {searchResults.map(b => (
                    <div
                      key={b.id}
                      onClick={() => navigate(`/business/${b.id}`)}
                      className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors"
                      data-testid={`claim-result-${b.id}`}
                    >
                      <div>
                        <p className="font-semibold text-gray-900">{b.businessName}</p>
                        <p className="text-sm text-gray-500">{b.category} — {formatPlaceName(b.city)}</p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-amber-600" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* List My Business fallback — for owners whose business isn't already seeded */}
          <div className="mt-6 pt-5 border-t border-gray-200 text-center" data-testid="claim-list-my-business-block">
            <p className="font-semibold text-gray-900 mb-1">
              {t('claimCantFind', "Can't find your business?")}
            </p>
            <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
              {t('claimListYourselfDesc', 'List your business directly on Find-2-Day and create your business profile.')}
            </p>
            <button
              onClick={() => navigate('/business/signup')}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl shadow-md hover:shadow-lg hover:from-amber-400 hover:to-orange-400 transition-all"
              data-testid="claim-list-my-business-btn"
            >
              <Store className="h-5 w-5" />
              {t('claimListMyBusinessBtn', 'List My Business')}
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="max-w-4xl mx-auto px-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-10" data-testid="how-it-works-title">
            {t('claimHowItWorks')}
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={i} className="text-center" data-testid={`claim-step-${i + 1}`}>
                  <div className="relative inline-flex items-center justify-center w-16 h-16 bg-amber-100 text-amber-600 rounded-full mb-4">
                    <Icon className="h-8 w-8" />
                    <span className="absolute -top-1 -right-1 w-6 h-6 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {i + 1}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
          {t('claimWhyClaim')}
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            t('claimBenefit1'),
            t('claimBenefit2'),
            t('claimBenefit3'),
            t('claimBenefit4'),
          ].map((benefit, i) => (
            <div key={i} className="flex items-start gap-3 bg-white p-5 rounded-xl shadow-sm border border-gray-100" data-testid={`claim-benefit-${i + 1}`}>
              <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700 font-medium">{benefit}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Contact fallback */}
      <div className="max-w-4xl mx-auto px-6 py-10 text-center">
        <p className="text-gray-500 text-sm">
          {t('claimNeedHelp')}{' '}
          <a href="mailto:find-2-day@outlook.com" className="text-amber-600 hover:underline font-medium">
            find-2-day@outlook.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default ClaimYourBusiness;
